import { useCallback, useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { ShieldCheck, Siren, HandPalm, Stop } from "@phosphor-icons/react";
import { toast } from "sonner";

function getLoc() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(p => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }), () => resolve({}), { timeout: 6000, maximumAge: 60000 });
  });
}

function fmtRemain(ms) {
  const neg = ms < 0; const s = Math.floor(Math.abs(ms) / 1000);
  return `${neg ? "-" : ""}${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Operative lone-worker timer: start → "I'm OK" before each due time → end. SOS alerts Ops instantly. */
export default function LoneWorkerCard({ entityType = "", entityId = "", compact = false }) {
  const [state, setState] = useState({ session: null, intervals: [15, 30, 45, 60, 90, 120], grace_minutes: 10 });
  const [interval, setIntervalMin] = useState(60);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [sosHold, setSosHold] = useState(0);
  const holdRef = useRef(null);
  const buzzedRef = useRef("");

  const load = useCallback(async () => {
    try { const { data } = await API.get("/in-house-portal/lone-worker/active"); setState(data); if (data.session) setIntervalMin(data.session.interval_minutes); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const s = state.session;
  const dueMs = s ? new Date(s.next_due_at).getTime() - now : 0;
  const overdue = s && (s.status === "overdue" || dueMs < 0);
  const dueSoon = s && !overdue && dueMs < 5 * 60 * 1000;

  useEffect(() => {
    if (!s) return;
    const key = `${s.id}:${overdue ? "over" : dueSoon ? "soon" : ""}`;
    if ((overdue || dueSoon) && buzzedRef.current !== key) { buzzedRef.current = key; navigator.vibrate?.(overdue ? [300, 100, 300, 100, 300] : [200]); }
  }, [s, overdue, dueSoon]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const loc = await getLoc(); await fn(loc); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const start = () => act(loc => API.post("/in-house-portal/lone-worker/start", { interval_minutes: interval, entity_type: entityType, entity_id: entityId, ...loc }), "Lone-worker timer started");
  const checkIn = () => act(loc => API.post(`/in-house-portal/lone-worker/${s.id}/check-in`, { interval_minutes: interval, ...loc }), "Checked in — stay safe");
  const end = () => act(loc => API.post(`/in-house-portal/lone-worker/${s.id}/end`, loc), "Session ended");
  const sos = () => act(loc => API.post("/in-house-portal/lone-worker/sos", { entity_type: entityType, entity_id: entityId, ...loc }), "SOS sent — Ops have been alerted");

  const holdStart = () => {
    const began = Date.now();
    holdRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - began) / 2000) * 100);
      setSosHold(pct);
      if (pct >= 100) { clearInterval(holdRef.current); setSosHold(0); sos(); }
    }, 50);
  };
  const holdEnd = () => { clearInterval(holdRef.current); setSosHold(0); };

  const tone = s?.status === "sos" ? "border-red-500 bg-red-50 dark:bg-red-950/30" : overdue ? "border-red-400 bg-red-50/60 dark:bg-red-950/20" : dueSoon ? "border-amber-400 bg-amber-50/60 dark:bg-amber-950/20" : s ? "border-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-border bg-card";

  return (
    <div className={`border-2 rounded-lg p-4 space-y-3 transition-colors ${tone}`} data-testid="lone-worker-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5" weight={s ? "fill" : "regular"} /> Lone worker</h2>
          {!s && !compact && <p className="text-xs text-muted-foreground mt-0.5">Working alone? Start a timer and tap "I'm OK" before it runs out. Miss it and Ops are alerted.</p>}
        </div>
        {s && (
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.status === "sos" ? "bg-red-600 text-white" : overdue ? "bg-red-500 text-white" : "bg-emerald-600 text-white"}`} data-testid="lone-worker-status">
            {s.status === "sos" ? "SOS sent" : overdue ? "Check-in overdue" : "Active"}
          </span>
        )}
      </div>

      {!s ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 flex-wrap" data-testid="lone-worker-intervals">
            {state.intervals.map(m => (
              <button key={m} type="button" onClick={() => setIntervalMin(m)} className={`px-2.5 py-1 rounded-full text-xs border ${interval === m ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background hover:bg-muted"}`} data-testid={`lone-worker-interval-${m}`}>{m} min</button>
            ))}
          </div>
          <Button size="sm" onClick={start} disabled={busy} data-testid="lone-worker-start">Start timer</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{overdue ? "Overdue by" : "Next check-in in"}</p>
              <p className={`text-3xl font-bold tabular-nums ${overdue ? "text-red-600" : dueSoon ? "text-amber-600" : ""}`} data-testid="lone-worker-countdown">{fmtRemain(dueMs)}</p>
            </div>
            {s.entity_title && <p className="text-xs text-muted-foreground pb-1 truncate max-w-[16rem]">{s.entity_title}</p>}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={checkIn} disabled={busy} className="rounded-full px-6" data-testid="lone-worker-checkin"><HandPalm className="h-4 w-4 mr-1.5" weight="fill" /> I'm OK</Button>
            <select value={interval} onChange={e => setIntervalMin(Number(e.target.value))} className="h-9 text-xs border border-border rounded-md bg-background px-2" data-testid="lone-worker-interval-select" title="Interval for the next check-in">
              {state.intervals.map(m => <option key={m} value={m}>every {m} min</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={end} disabled={busy} data-testid="lone-worker-end"><Stop className="h-4 w-4 mr-1" /> End</Button>
          </div>
        </div>
      )}

      <div className="pt-1">
        <button type="button"
                onPointerDown={holdStart} onPointerUp={holdEnd} onPointerLeave={holdEnd} onPointerCancel={holdEnd}
                disabled={busy || s?.status === "sos"}
                className="relative w-full overflow-hidden rounded-md border-2 border-red-600 bg-red-600 text-white font-bold py-3 select-none touch-none disabled:opacity-60"
                data-testid="lone-worker-sos">
          <span className="absolute inset-y-0 left-0 bg-red-900/60" style={{ width: `${sosHold}%` }} />
          <span className="relative inline-flex items-center gap-2"><Siren className="h-5 w-5" weight="fill" /> {s?.status === "sos" ? "SOS sent — help is on the way" : "Hold for SOS"}</span>
        </button>
        {!compact && <p className="text-[10px] text-muted-foreground mt-1 text-center">Hold for 2 seconds. Alerts your Operations Directors with your location. In immediate danger call 999 first.</p>}
      </div>
    </div>
  );
}
